import { useEffect, useCallback } from 'react';
import { usePlatform } from '@/hooks/usePlatform';

/**
 * Componente que corrige el desfase de coordenadas táctiles en Android.
 * 
 * PROBLEMA: En algunos dispositivos Android con alto DPR, las coordenadas
 * táctiles (touch.clientX/Y) están escaladas por el DPR, pero el layout
 * CSS no lo está. Esto causa que los toques "físicos" no coincidan con
 * los elementos visuales.
 * 
 * SOLUCIÓN: Interceptar TODOS los eventos táctiles a nivel de documento
 * y calcular si el toque "corregido" está en la zona del botón INICIO.
 * Si es así, disparar el evento de cierre del WebView.
 */
export const TouchCoordinateFixer = () => {
    const { isAndroid } = usePlatform();

    // Función para verificar si un WebView está abierto
    const isWebViewOpen = useCallback(() => {
        return document.querySelector('.webview-container') !== null;
    }, []);

    // Función para cerrar el WebView
    const closeWebView = useCallback(() => {
        console.log('🎯 TouchCoordinateFixer: Cerrando WebView');
        window.dispatchEvent(new Event('closeWebView'));
    }, []);

    useEffect(() => {
        if (!isAndroid) return;

        const dpr = window.devicePixelRatio || 1;

        console.log('📱 TouchCoordinateFixer inicializado:', {
            dpr,
            screenWidth: window.screen.width,
            screenHeight: window.screen.height,
            innerWidth: window.innerWidth,
            innerHeight: window.innerHeight
        });

        // Si el DPR es 1 o cercano, no hay problema de escala
        if (dpr < 1.5) {
            console.log('✅ DPR bajo, no se requiere corrección de coordenadas');
            return;
        }

        const handleTouchEnd = (e: TouchEvent) => {
            // Solo procesar si hay un WebView abierto
            if (!isWebViewOpen()) return;

            const touch = e.changedTouches[0];
            if (!touch) return;

            // Obtener las coordenadas del toque
            // En algunos dispositivos, clientX/Y ya están en coordenadas CSS
            // En otros, están en coordenadas físicas (multiplicadas por DPR)
            const rawX = touch.clientX;
            const rawY = touch.clientY;

            // También obtener las coordenadas de pantalla
            const screenX = touch.screenX;
            const screenY = touch.screenY;

            // Dimensiones de la ventana
            const windowWidth = window.innerWidth;
            const windowHeight = window.innerHeight;

            // Log para debug
            console.log('👆 Touch detectado:', {
                rawX, rawY,
                screenX, screenY,
                windowWidth, windowHeight,
                dpr,
                // Calcular también las coordenadas "corregidas"
                correctedX: rawX / dpr,
                correctedY: rawY / dpr
            });

            // El botón INICIO está en:
            // - Horizontalmente: centrado (25% - 75% del ancho)
            // - Verticalmente: en el 15% inferior (85% - 100% de la altura)

            // Calcular los límites del botón en coordenadas CSS
            const buttonLeft = windowWidth * 0.20;  // 20% desde izquierda
            const buttonRight = windowWidth * 0.80; // 80% desde izquierda
            const buttonTop = windowHeight * 0.85;  // 85% desde arriba
            const buttonBottom = windowHeight;      // 100%

            // Verificar si el toque está en la zona del botón
            // Intentamos con coordenadas RAW primero
            let inButtonZone = (
                rawX >= buttonLeft && rawX <= buttonRight &&
                rawY >= buttonTop && rawY <= buttonBottom
            );

            // Si no está en la zona con coordenadas raw, intentar con coordenadas corregidas
            if (!inButtonZone && dpr > 1.5) {
                const correctedX = rawX / dpr;
                const correctedY = rawY / dpr;

                inButtonZone = (
                    correctedX >= buttonLeft && correctedX <= buttonRight &&
                    correctedY >= buttonTop && correctedY <= buttonBottom
                );

                if (inButtonZone) {
                    console.log('🔧 Toque corregido por DPR detectado en zona de botón');
                }
            }

            // También verificar con coordenadas de pantalla escaladas
            if (!inButtonZone) {
                // Las coordenadas de pantalla pueden estar en píxeles físicos
                const scaledX = screenX / dpr;
                const scaledY = screenY / dpr;

                // Ajustar por la posición de la ventana en la pantalla
                // (simplificación: asumimos que está a pantalla completa)
                inButtonZone = (
                    scaledX >= buttonLeft && scaledX <= buttonRight &&
                    scaledY >= buttonTop && scaledY <= buttonBottom
                );

                if (inButtonZone) {
                    console.log('🔧 Toque con screenX/Y escalado detectado en zona de botón');
                }
            }

            if (inButtonZone) {
                console.log('🏠 ¡Toque en zona de botón INICIO! Cerrando WebView...');
                e.preventDefault();
                e.stopPropagation();
                closeWebView();
            }
        };

        // Registrar el listener en fase de captura con passive: false
        document.addEventListener('touchend', handleTouchEnd, {
            capture: true,
            passive: false
        });

        console.log('🔧 TouchCoordinateFixer: Listener de corrección activado');

        return () => {
            document.removeEventListener('touchend', handleTouchEnd, { capture: true });
        };
    }, [isAndroid, isWebViewOpen, closeWebView]);

    return null;
};

